import { Filter, SearchInput, SelectInput } from 'react-admin';
import { useState } from 'react';
import { Box, Typography, Paper, Chip, Table, TableBody, TableCell, TableHead, TableRow, Avatar, TablePagination, useMediaQuery } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Business } from '@mui/icons-material';
import { useGetList, useNavigate } from 'react-admin';
import { ListPageHeader } from '@/components/ListPageHeader';
import { STATUS_COLORS_PROVIDER, PROVIDER_ACCREDITATION_STATUS, PROVIDER_STATUS } from './constants';

interface ProviderItem {
  id: string;
  status: string;
  providerName: string;
  accreditationStatus?: string;
  setaAccreditationNumber?: string;
  accreditationExpiry?: string;
  legalEntity?: {
    companyName?: string;
    email?: string;
    phone?: string;
  };
  performanceMetrics?: {
    coursesDelivered?: number;
    totalLearners?: number;
  };
}

export const TrainingProviderList = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: providers, isLoading } = useGetList('training-providers', { pagination: { page: page + 1, perPage: rowsPerPage } });
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const stats = {
    total: providers?.length ?? 0,
    active: providers?.filter((p: ProviderItem) => p.status === 'Active').length ?? 0,
    pending: providers?.filter((p: ProviderItem) => p.status === 'Pending' || p.accreditationStatus === 'Pending').length ?? 0,
    suspended: providers?.filter((p: ProviderItem) => p.status === 'Suspended' || p.accreditationStatus === 'Suspended').length ?? 0,
    expired: providers?.filter((p: ProviderItem) => p.accreditationStatus === 'Expired' || p.accreditationStatus === 'Revoked').length ?? 0,
  };

  const pieData = [
    { label: 'Active', value: stats.active },
    { label: 'Pending', value: stats.pending },
    { label: 'Suspended', value: stats.suspended },
    { label: 'Expired', value: stats.expired },
  ].filter(d => d.value > 0);

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <ListPageHeader
          title="Training Providers"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'success.main' },
            { label: 'Pending', value: String(stats.pending), color: 'info.main' },
            { label: 'Suspended', value: String(stats.suspended), color: 'warning.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {providers?.map((provider: ProviderItem) => (
            <Paper key={provider.id} sx={{ p: 2 }} onClick={() => navigate(`/training-providers/${provider.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main', fontSize: 14 }}>
                    <Business sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{provider.providerName}</Typography>
                    <Typography variant="caption" color="text.secondary">{provider.setaAccreditationNumber}</Typography>
                  </Box>
                </Box>
                <Chip label={provider.accreditationStatus} size="small" color={STATUS_COLORS_PROVIDER[provider.accreditationStatus ?? ''] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Typography variant="caption" color="text.secondary">{provider.legalEntity?.companyName || '—'}</Typography>
                <Typography variant="caption" color="text.secondary">·</Typography>
                <Typography variant="caption" color="text.secondary">{provider.accreditationExpiry || '—'}</Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <ListPageHeader
        title="Training Providers"
        metrics={[
          { label: 'Active', value: String(stats.active), color: 'success.main' },
          { label: 'Pending', value: String(stats.pending), color: 'info.main' },
          { label: 'Suspended', value: String(stats.suspended), color: 'warning.main' },
          { label: 'Total', value: String(stats.total), color: 'primary.main' },
        ]}
        pieChartData={pieData}
        pieChartTitle="Provider Status"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Provider</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>SETA Accreditation</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Contact Person</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Expiry</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Courses</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Learners</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {providers?.map((provider: ProviderItem) => (
              <TableRow
                key={provider.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/training-providers/${provider.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main', fontSize: 12 }}>
                      <Business sx={{ fontSize: 16 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{provider.providerName}</Typography>
                      <Typography variant="caption" color="text.secondary">{provider.legalEntity?.email}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{provider.setaAccreditationNumber || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{provider.legalEntity?.companyName || '—'}</Typography>
                </TableCell>
                <TableCell>
                <Chip label={provider.accreditationStatus} size="small" color={STATUS_COLORS_PROVIDER[provider.accreditationStatus ?? ''] as ChipColor} />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{provider.accreditationExpiry || '—'}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{provider.performanceMetrics?.coursesDelivered ?? 0}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{provider.performanceMetrics?.totalLearners ?? 0}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={providers?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!providers || providers.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Business sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No training providers found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const TrainingProviderFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search provider name or SETA number" />
    <SelectInput source="accreditationStatus" choices={PROVIDER_ACCREDITATION_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
    <SelectInput source="status" choices={PROVIDER_STATUS.map(s => ({ id: s, name: s }))} />
  </Filter>
);
